import React, { useMemo } from "react";

const CircleSequence = ({
  sequence,               // array of nail indices, e.g. [0, 47, 123, ...]
  nailCount = 250,        // total number of nails on the circle
  lineWidth = 0.8,
  positionToShow,         // how many steps to draw (defaults to full sequence)
  size = 600,             // SVG viewport size in px
  darkMode = false,
}) => {
  const margin = 52;
  const radius = size / 2 - margin;
  const center = size / 2;

  const bg       = darkMode ? "#0a0a0a" : "#ffffff";
  const circleStroke = darkMode ? "#222222" : "#cccccc";
  const markerStroke = darkMode ? "#333333" : "#999999";
  const markerText   = darkMode ? "#555555" : "#888888";
  const lineStroke   = darkMode ? "#e8e8e8" : "#111111";
  const dotColor     = darkMode ? "#3366ff" : "#2255dd";

  // Posición del clavo: índice 0 a la derecha, sentido horario. Es la misma
  // convención que usan SequencePreview y CircleSequence de la app, para que la
  // secuencia generada se vea igual aquí que en el resto del programa.
  const getPos = (nailIdx) => {
    const angle = (2 * Math.PI * nailIdx) / nailCount;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    };
  };

  const maxPos = positionToShow != null
    ? Math.min(positionToShow, sequence.length - 1)
    : sequence.length - 1;

  const sub = useMemo(
    () => (maxPos < 0 ? [] : sequence.slice(0, maxPos + 1)),
    [sequence, maxPos]
  );

  const lastIdx         = sub.length - 1;
  const secondLastIdx   = lastIdx - 1;

  // Lines
  const lines = useMemo(() => sub.slice(0, -1).map((nail, i) => {
    const a = getPos(nail);
    const b = getPos(sub[i + 1]);
    return (
      <line key={i}
        x1={a.x} y1={a.y} x2={b.x} y2={b.y}
        stroke={lineStroke} strokeWidth={lineWidth} strokeOpacity={0.55}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [sub, lineWidth, lineStroke]);

  // Nail dots
  const dots = useMemo(() => Array.from({ length: nailCount }, (_, i) => {
    const { x, y } = getPos(i);
    const isLast       = i === sub[lastIdx];
    const isSecondLast = i === sub[secondLastIdx];
    const r = isLast || isSecondLast ? 4 : 2;
    const fill = isLast || isSecondLast ? "#ff4444" : dotColor;
    return (
      <circle key={i} cx={x} cy={y} r={r} fill={fill} />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [nailCount, sub, lastIdx, secondLastIdx, dotColor]);

  // Numeric markers every 10 nails
  const markerStep = nailCount <= 100 ? 5 : 10;
  const markers = useMemo(() => {
    const arr = [];
    for (let i = 0; i < nailCount; i += markerStep) {
      const angle = (2 * Math.PI * i) / nailCount;
      const cos = Math.cos(angle), sin = Math.sin(angle);
      arr.push({
        x1: center + radius * cos,
        y1: center + radius * sin,
        x2: center + (radius + 10) * cos,
        y2: center + (radius + 10) * sin,
        tx: center + (radius + 24) * cos,
        ty: center + (radius + 24) * sin,
        label: i,
      });
    }
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nailCount, markerStep, radius, center]);

  // Arrow pointing at last drawn nail
  let arrow = null;
  if (lastIdx >= 0) {
    const lastNail = sub[lastIdx];
    const { x: lx, y: ly } = getPos(lastNail);
    const angle = (2 * Math.PI * lastNail) / nailCount;
    const ar = radius + 38;
    const ax = center + ar * Math.cos(angle);
    const ay = center + ar * Math.sin(angle);
    arrow = (
      <line
        x1={ax} y1={ay} x2={lx} y2={ly}
        stroke="#ff4444" strokeWidth={3}
        markerEnd="url(#arrow)"
      />
    );
  }

  return (
    <div style={{ width: "100%", background: bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}
      >
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
            <path d="M0,0 L0,8 L7,4 z" fill="#ff4444" />
          </marker>
        </defs>

        {/* Background */}
        <rect width={size} height={size} fill={bg} />

        {/* Reference circle */}
        <circle cx={center} cy={center} r={radius} fill="none" stroke={circleStroke} strokeWidth={1} />

        {/* String lines */}
        {lines}

        {/* Nail dots */}
        {dots}

        {/* Markers */}
        {markers.map(({ x1, y1, x2, y2, tx, ty, label }, i) => (
          <g key={i}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={markerStroke} strokeWidth={1} />
            <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle"
              fontSize={11} fill={markerText} fontFamily="IBM Plex Mono, monospace">
              {label}
            </text>
          </g>
        ))}

        {/* Arrow to last nail */}
        {arrow}

        {/* Info label */}
        {sub.length > 1 && (
          <text x={center} y={size - 10} textAnchor="middle"
            fontSize={10} fill={markerText} fontFamily="IBM Plex Mono, monospace">
            {sub.length - 1} líneas · {nailCount} clavos
          </text>
        )}
      </svg>
    </div>
  );
};

export default CircleSequence;
